import { useEffect, useLayoutEffect, useRef, useState } from "react";
import { PMPagination } from "../../utils/PMPagination";
import { Link, useNavigate } from "react-router-dom";
import { LoadingRows, ItemStatus, dateToString, CustomerTitle, ProjectTitle } from "../../Utils";
import { ResourceEmpty } from "../../utils/ResourceEmpty";
import { Notes } from "../notes/notes";
import { useSettings } from "../../../contexts/settings-context";
import { 
  PencilIcon,
  BuildingOffice2Icon,
  UserIcon,
  CheckCircleIcon,
  DocumentTextIcon,
  ClockIcon,
  MapPinIcon,
  CalendarIcon,
  CurrencyDollarIcon,
  ChartBarIcon,
  ArrowTrendingUpIcon,
  ArrowTrendingDownIcon
} from "@heroicons/react/24/outline";
import { useTranslation } from "react-i18next";
import { OrderStatus } from "../orders/utils";

// Helper function to calculate statistics from offers
const calculateStatistics = (offers) => {
  let totalSales = 0;
  let totalCost = 0;
  let totalHours = 0;
  let totalCount = offers?.length || 0;
  let activeCount = 0;
  let sentCount = 0;
  let acceptedCount = 0;

  if (offers && offers.length > 0) {
    offers.forEach((offer) => {
      if (offer.active) activeCount++;
      if (offer.status === 'sent') sentCount++;
      if (offer.status === 'accepted') acceptedCount++;
      
      const salesPrice = parseFloat(offer.total_amount || 0);
      totalSales += salesPrice;
      
      // Calculate cost from items if available
      if (offer.items && offer.items.length > 0) {
        offer.items.forEach((item) => {
          const costPrice = parseFloat(item.purchase_price || 0);
          const quantity = parseFloat(item.quantity || 1);
          totalCost += costPrice * quantity;
          
          // Calculate hours if available
          if (item.hours) {
            const hours = parseFloat(item.hours || 0);
            totalHours += hours * quantity;
          }
        });
      }
    });
  }

  const totalMargin = totalSales - totalCost;
  const marginPercent = totalSales > 0 ? (totalMargin / totalSales) * 100 : 0;

  return {
    totalSales,
    totalCost,
    totalMargin,
    marginPercent,
    totalHours,
    totalCount,
    activeCount,
    sentCount,
    acceptedCount,
  };
};

export const OffersTable = (props) => {
  const { itemState, onPageChange, itemsCount, page, perPage, currentCount, onItemsSelect } =
    props;
  const { data, isLoading, errors } = itemState;
  const { t } = useTranslation();
  const { priceFormatter } = useSettings();
  const navigate = useNavigate();

  const checkbox = useRef();
  const [checked, setChecked] = useState(false);
  const [indeterminate, setIndeterminate] = useState(false);
  const [selectedItems, setSelectedItems] = useState([]);

  const statistics = calculateStatistics(data);

  useLayoutEffect(() => {
    const isIndeterminate =
      selectedItems?.length > 0 && selectedItems?.length < data?.length;
    setChecked(selectedItems?.length === data?.length);
    setIndeterminate(isIndeterminate);
    if (checkbox.current) {
      checkbox.current.indeterminate = isIndeterminate;
    }
    if (onItemsSelect) {
      onItemsSelect(selectedItems);
    }
  }, [selectedItems, data?.length, onItemsSelect]);

  function toggleAll() {
    setSelectedItems(checked || indeterminate ? [] : data);
    setChecked(!checked && !indeterminate);
    setIndeterminate(false);
  }

  const OfferCard = ({ item }) => {
    // Calculate item statistics for this offer
    let itemTotalSales = parseFloat(item.total_amount || 0);
    let itemTotalCost = 0;
    let itemTotalHours = 0;

    if (item.items && item.items.length > 0) {
      item.items.forEach((itemRow) => {
        const costPrice = parseFloat(itemRow.purchase_price || 0);
        const quantity = parseFloat(itemRow.quantity || 1);
        itemTotalCost += costPrice * quantity;
        
        if (itemRow.hours) {
          const hours = parseFloat(itemRow.hours || 0);
          itemTotalHours += hours * quantity;
        }
      });
    }

    const itemMargin = itemTotalSales - itemTotalCost;
    const itemMarginPercent = itemTotalSales > 0 ? (itemMargin / itemTotalSales) * 100 : 0;

    return (
      <div 
        className={`shadow sm:overflow-hidden sm:rounded-md hover:shadow-lg transition-shadow duration-200 ${
          selectedItems.includes(item) ? "ring-2 ring-blue-400" : ""
        }`}
      >
        <div className="bg-gradient-to-r from-indigo-50 to-purple-50 px-6 py-4 border-b border-gray-200">
          <div className="flex items-center justify-between">
            <div className="flex items-center">
              <div 
                className="flex-shrink-0 cursor-pointer hover:scale-105 transition-transform duration-200"
                onClick={() => navigate("/dashboard/offers/" + item?.id)}
              >
                <div className="w-10 h-10 bg-indigo-100 rounded-lg flex items-center justify-center">
                  <DocumentTextIcon className="w-6 h-6 text-indigo-600" />
                </div>
              </div>
              <div className="ml-4">
                <div className="flex items-center space-x-3">
                  <h3 
                    className="text-lg font-semibold text-gray-900 cursor-pointer hover:text-indigo-600 transition-colors duration-200"
                    onClick={() => navigate("/dashboard/offers/" + item?.id)}
                  >
                    {item?.offer_number || `Tilbud #${item?.id}`}
                  </h3>
                  <OrderStatus order={item} />
                </div>
                <p className="text-sm text-gray-600">
                  {dateToString(item?.offer_date)}
                </p>
              </div>
            </div>
            <div className="flex items-center space-x-2">
              {item?.customer && (
                <span className="inline-flex items-center px-2 py-1 rounded-full text-xs font-medium bg-green-100 text-green-800">
                  <BuildingOffice2Icon className="w-3 h-3 mr-1" />
                  {item.customer.displayTitle || item.customer.name}
                </span>
              )}
            </div>
          </div>
        </div>
        
        <div className="bg-white px-6 py-6">
          {/* Statistics Row */}
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-6 pb-6 border-b border-gray-200">
            <div className="flex flex-col">
              <div className="text-xs text-gray-500 flex items-center mb-1">
                <CurrencyDollarIcon className="w-3 h-3 mr-1" />
                {t("Salgspris")}
              </div>
              <div className="text-lg font-semibold text-gray-900">
                {priceFormatter.format(itemTotalSales)}
              </div>
            </div>
            <div className="flex flex-col">
              <div className="text-xs text-gray-500 flex items-center mb-1">
                <ArrowTrendingDownIcon className="w-3 h-3 mr-1" />
                {t("Kostnad")}
              </div>
              <div className="text-lg font-semibold text-gray-900">
                {priceFormatter.format(itemTotalCost)}
              </div>
            </div>
            <div className="flex flex-col">
              <div className="text-xs text-gray-500 flex items-center mb-1">
                <ArrowTrendingUpIcon className={`w-3 h-3 mr-1 ${itemMargin >= 0 ? 'text-green-500' : 'text-red-500'}`} />
                {t("Margin")}
              </div>
              <div className={`text-lg font-semibold ${itemMargin >= 0 ? 'text-green-600' : 'text-red-600'}`}>
                {priceFormatter.format(itemMargin)} ({itemMarginPercent.toFixed(1)}%)
              </div>
            </div>
            <div className="flex flex-col">
              <div className="text-xs text-gray-500 flex items-center mb-1">
                <ClockIcon className="w-3 h-3 mr-1" />
                {t("Timer")}
              </div>
              <div className="text-lg font-semibold text-gray-900">
                {itemTotalHours.toFixed(1)}t
              </div>
            </div>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {/* Customer Information */}
            {item?.customer && (
              <div>
                <dt className="text-sm font-medium text-gray-500 flex items-center">
                  <BuildingOffice2Icon className="w-4 h-4 mr-1" />
                  {t("Customer")}
                </dt>
                <dd className="mt-1 text-sm text-gray-900">
                  <Link
                    to={"/dashboard/customers/" + item?.customer?.id}
                    className="text-blue-400 hover:text-blue-600"
                  >
                    {item.customer.displayTitle || item.customer.name || "-"}
                  </Link>
                </dd>
              </div>
            )}

            {/* Project */}
            {item?.project && (
              <div>
                <dt className="text-sm font-medium text-gray-500 flex items-center">
                  <DocumentTextIcon className="w-4 h-4 mr-1" />
                  {t("Project")}
                </dt>
                <dd className="mt-1 text-sm text-gray-900">
                  <Link
                    to={"/dashboard/projects/" + item?.project?.id}
                    className="text-blue-400 hover:text-blue-600"
                  >
                    {item.project.name || item.project.project_number || "-"}
                  </Link>
                </dd>
              </div>
            )}

            {/* Contact */}
            {item?.contact && (
              <div>
                <dt className="text-sm font-medium text-gray-500 flex items-center">
                  <UserIcon className="w-4 h-4 mr-1" />
                  {t("Contact")}
                </dt>
                <dd className="mt-1 text-sm text-gray-900">
                  {item.contact.displayTitle || item.contact.name || "-"}
                </dd>
              </div>
            )}

            {/* Offer Date */}
            {item?.offer_date && (
              <div>
                <dt className="text-sm font-medium text-gray-500 flex items-center">
                  <CalendarIcon className="w-4 h-4 mr-1" />
                  {t("Offer Date")}
                </dt>
                <dd className="mt-1 text-sm text-gray-900">
                  {dateToString(item.offer_date)}
                </dd>
              </div>
            )}

            {/* Delivery Date */}
            {item?.delivery_date && (
              <div>
                <dt className="text-sm font-medium text-gray-500 flex items-center">
                  <CalendarIcon className="w-4 h-4 mr-1" />
                  {t("Delivery Date")}
                </dt>
                <dd className="mt-1 text-sm text-gray-900">
                  {dateToString(item.delivery_date)}
                </dd>
              </div>
            )}

            {/* Reference */}
            {item?.reference && (
              <div>
                <dt className="text-sm font-medium text-gray-500 flex items-center">
                  <DocumentTextIcon className="w-4 h-4 mr-1" />
                  {t("Reference")}
                </dt>
                <dd className="mt-1 text-sm text-gray-900">
                  {item.reference}
                </dd>
              </div>
            )}
          </div>

          {/* Quick Actions */}
          <div className="mt-6 flex items-center justify-between">
            <div className="flex items-center space-x-2">
              <input
                type="checkbox"
                className="h-4 w-4 rounded border-gray-300 text-blue-400 focus:ring-blue-400"
                value={item.id}
                checked={selectedItems.includes(item)}
                onChange={(e) => {
                  setSelectedItems(
                    e.target.checked
                      ? [...selectedItems, item]
                      : selectedItems.filter((p) => p !== item)
                  );
                }}
              />
              <span className="text-sm text-gray-500">{t("Select")}</span>
            </div>
            <div className="flex items-center space-x-2">
              <button
                onClick={() => navigate("/dashboard/offers/" + item.id)}
                className="inline-flex items-center px-3 py-2 border border-gray-300 shadow-sm text-sm leading-4 font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500"
              >
                <PencilIcon className="h-4 w-4 mr-1" />
                {t("Edit")}
              </button>
              <Notes module="offer" module_param={item.id} />
            </div>
          </div>
        </div>
      </div>
    );
  };

  const Stats = () => {
    return (
      <>
        {data && data.length > 0 && (
          <div className="space-y-4 mt-5">
            {data?.map((item) => (
              <OfferCard key={item?.id} item={item} />
            ))}
          </div>
        )}
      </>
    );
  };

  return (
    <div className="divide-y divide-gray-200 overflow-hidden rounded-lg sm:gap-px sm:divide-y-0">
      {/* Statistics Overview */}
      {data && data.length > 0 && (
        <div className="bg-white shadow sm:rounded-md mb-6">
          <div className="px-4 py-5 sm:p-6">
            <h3 className="text-lg font-medium leading-6 text-gray-900 mb-4">
              {t("Oversikt")}
            </h3>
            <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-7 gap-4">
              <div className="flex flex-col items-center p-4 bg-gray-50 rounded-lg">
                <CurrencyDollarIcon className="h-6 w-6 text-green-500 mb-2" />
                <p className="text-xs text-gray-600">{t("Total Salg")}</p>
                <p className="text-lg font-bold">{priceFormatter.format(statistics.totalSales)}</p>
              </div>
              <div className="flex flex-col items-center p-4 bg-gray-50 rounded-lg">
                <ArrowTrendingDownIcon className="h-6 w-6 text-red-500 mb-2" />
                <p className="text-xs text-gray-600">{t("Total Kostnad")}</p>
                <p className="text-lg font-bold">{priceFormatter.format(statistics.totalCost)}</p>
              </div>
              <div className="flex flex-col items-center p-4 bg-gray-50 rounded-lg">
                <ArrowTrendingUpIcon className={`h-6 w-6 mb-2 ${statistics.totalMargin >= 0 ? 'text-green-500' : 'text-red-500'}`} />
                <p className="text-xs text-gray-600">{t("Total Margin")}</p>
                <p className={`text-lg font-bold ${statistics.totalMargin >= 0 ? 'text-green-600' : 'text-red-600'}`}>
                  {priceFormatter.format(statistics.totalMargin)}
                </p>
                <p className="text-xs text-gray-500">({statistics.marginPercent.toFixed(1)}%)</p>
              </div>
              <div className="flex flex-col items-center p-4 bg-gray-50 rounded-lg">
                <ClockIcon className="h-6 w-6 text-blue-500 mb-2" />
                <p className="text-xs text-gray-600">{t("Total Timer")}</p>
                <p className="text-lg font-bold">{statistics.totalHours.toFixed(1)}t</p>
              </div>
              <div className="flex flex-col items-center p-4 bg-gray-50 rounded-lg">
                <DocumentTextIcon className="h-6 w-6 text-purple-500 mb-2" />
                <p className="text-xs text-gray-600">{t("Totalt Antall")}</p>
                <p className="text-lg font-bold">{statistics.totalCount}</p>
              </div>
              <div className="flex flex-col items-center p-4 bg-gray-50 rounded-lg">
                <CheckCircleIcon className="h-6 w-6 text-green-500 mb-2" />
                <p className="text-xs text-gray-600">{t("Aktive")}</p>
                <p className="text-lg font-bold">{statistics.activeCount}</p>
              </div>
              <div className="flex flex-col items-center p-4 bg-gray-50 rounded-lg">
                <ChartBarIcon className="h-6 w-6 text-blue-500 mb-2" />
                <p className="text-xs text-gray-600">{t("Sendt")}</p>
                <p className="text-lg font-bold">{statistics.sentCount}</p>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Selection bar */}
      {selectedItems?.length > 0 && (
        <div className="relative px-5 top-0 flex h-12 items-center space-x-3 bg-blue-400 rounded-xl mb-4 text-white">
          <div className="flex w-full items-center justify-between">
            <div className="">
              <span>{selectedItems.length} {t("selected")}</span>
            </div>
            <div className="px-2 py-2">
              <button
                type="button"
                className="inline-flex items-center rounded bg-white px-2 py-1 text-sm font-semibold text-gray-900 shadow-sm ring-1 ring-inset ring-gray-300 hover:bg-gray-50 disabled:cursor-not-allowed disabled:opacity-30 disabled:hover:bg-white"
              >
                {t("Edit")}
              </button>
              <button
                type="button"
                className="inline-flex items-center rounded bg-white px-2 py-1 ml-2 text-sm font-semibold text-gray-900 shadow-sm ring-1 ring-inset ring-gray-300 hover:bg-gray-50 disabled:cursor-not-allowed disabled:opacity-30 disabled:hover:bg-white"
              >
                {t("Finish")}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Select all checkbox */}
      <div className="mb-4">
        <div className="flex items-center">
          <input
            type="checkbox"
            className="h-4 w-4 rounded border-gray-300 text-blue-400 focus:ring-blue-400"
            ref={checkbox}
            checked={checked}
            onChange={toggleAll}
          />
          <label className="ml-2 text-sm font-medium text-gray-700">
            {t("Select all")}
          </label>
        </div>
      </div>

      <div className="shadow sm:overflow-hidden sm:rounded-md">
        <div className="space-y-6 bg-white py-6 px-4 sm:p-6">
          {isLoading ? (
            <>
              <div className="bg-white border rounded-lg shadow-sm p-6 animate-pulse">
                <div className="flex items-center space-x-3">
                  <div className="h-4 w-4 bg-gray-200 rounded"></div>
                  <div className="flex-1">
                    <div className="h-4 bg-gray-200 rounded w-1/4 mb-2"></div>
                    <div className="h-4 bg-gray-200 rounded w-1/2 mb-2"></div>
                    <div className="h-3 bg-gray-200 rounded w-1/3"></div>
                  </div>
                  <div className="flex space-x-2">
                    <div className="h-8 w-16 bg-gray-200 rounded"></div>
                    <div className="h-8 w-8 bg-gray-200 rounded"></div>
                  </div>
                </div>
              </div>
              <div className="bg-white border rounded-lg shadow-sm p-6 animate-pulse">
                <div className="flex items-center space-x-3">
                  <div className="h-4 w-4 bg-gray-200 rounded"></div>
                  <div className="flex-1">
                    <div className="h-4 bg-gray-200 rounded w-1/4 mb-2"></div>
                    <div className="h-4 bg-gray-200 rounded w-1/2 mb-2"></div>
                    <div className="h-3 bg-gray-200 rounded w-1/3"></div>
                  </div>
                  <div className="flex space-x-2">
                    <div className="h-8 w-16 bg-gray-200 rounded"></div>
                    <div className="h-8 w-8 bg-gray-200 rounded"></div>
                  </div>
                </div>
              </div>
            </>
          ) : errors ? (
            <div className="text-center py-8 text-red-500">
              <div className="text-lg">{t("Could not load offers")}</div>
            </div>
          ) : data && data.length > 0 ? (
            <Stats />
          ) : (
            <div className="text-center py-8 text-gray-500">
              <div className="text-lg">{t("No offers found")}</div>
              <div className="text-sm">{t("Create a new offer")}</div>
            </div>
          )}
        </div>
      </div>

      <PMPagination
        disabled={isLoading}
        onPageChange={onPageChange}
        page={page}
        perPage={perPage}
        rowsCount={itemsCount}
        totalCount={itemsCount}
        currentCount={currentCount}
        showCount={true}
      />
    </div>
  );
};

OffersTable.defaultProps = {
  page: 1,
  itemState: [],
  itemsCount: 0,
  selectedItems: [],
  sort: "desc",
  sortBy: "createdAt",
  onItemsSelect: () => {},
};

